import axios from "@axios";
import {useRouter} from "@core/utils/utils";
import {computed} from "@vue/composition-api";
import Swal from "sweetalert2";
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { PROJECT_WON_STATUS, VALIDATION_REQUIRED_TYPES } from "@/constants/bid";

export default function useBidDetails(props, emit) {
    const {router} = useRouter();
    const toast = useToast();

    const showAlertWithAction = (title, text, successMessage, action = '', payload = {}, icon = "warning", toConfirm = true) => {
        Swal.fire({
            title: title,
            text: text,
            icon: icon,
            showCancelButton: true,
            confirmButtonText: "Oui, continuer!",
            cancelButtonText: "Annuler",
            customClass: {
                confirmButton: "btn btn-primary",
                cancelButton: "btn btn-outline-danger ml-1",
            },
            buttonsStyling: false,
            ...(toConfirm ? {
                preConfirm() {
                    return store.dispatch(action, payload)
                        .then(({data, success}) => {
                            if (!success) throw new Error(data.message);
                            router.go(0)
                            return data.resource;
                        }).catch((error) => {
                            Swal.showValidationMessage(
                                "Une erreur est survenue lors de la modification."
                            );
                        })
                }
            } : {})
        }).then((result) => {
            if (result.value) {
                Swal.fire({
                    icon: "success",
                    title: "Succès!",
                    text: successMessage,
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                }).then((result) => {
                    router.go(-1);
                });
            }
        });
    }
    const toggleAsLost = () => {
        Swal.fire({
            title: "Êtes vous sûr?",
            text: "Cette offre apparêtra désormais comme perdue, et donc ne fera plus sujet de dépenses!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, continuer!",
            cancelButtonText: "Annuler",
            customClass: {
                confirmButton: "btn btn-primary",
                cancelButton: "btn btn-outline-danger ml-1",
            },
            buttonsStyling: false,
            preConfirm() {
                const uid = props.bidData?.value.uid;
                return axios
                    .put(`toggle/projects/${uid}/lost`, {
                        headers: {
                            Accept: "application/vnd.api+json",
                            "Content-Type": "application/vnd.api+json",
                        },
                    })
                    .then(({data, status}) => {
                        if (status != 200) throw new Error(data.message);
                        return data.resource;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(
                            "Une erreur est survenue lors de la modification."
                        );
                    });
            },
        }).then((result) => {
            if (result.value) {
                Swal.fire({
                    icon: "success",
                    title: "Succès!",
                    text: "Ce projet est considéré comme perdu.",
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                }).then((result) => {
                    router.go(-1);
                });
            }
        });
    };

    const toggleAsWin = () => {
        // Check if the type requires client validation
        const bidType = props.bidData?.value?.type?.toLowerCase();
        const requiresValidation = VALIDATION_REQUIRED_TYPES().includes(bidType);

        // If validation required, check the status
        if (requiresValidation) {
            const clientValidationStatus = props.bidData?.value?.inventory?.client_validation_status;

            if (clientValidationStatus !== 'pending_client_validation') {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Validation requise",
                        text: "Vous devez d'abord notifier le client pour validation du devis",
                        icon: "AlertTriangleIcon",
                        variant: "warning",
                    },
                });
                return;
            }

            // Check that the quote has been validated by the client
            // Note: The backend API will handle this verification and return a 422 error if not validated
        }

        showAlertWithAction(
            "Êtes vous sûr?",
            "Cette offre apparêtra désormais comme gagné, et donc, va générer un nouveau projet dans le module Projet!",
            "Cette offre est considérée comme gagné.",
            'bid/setOpportunityStatusToRealisation',
            { id: props.bidData?.value.uid }
        );
    };

    const closeProject = () => {
        let bidData = props.bidData.value
        let budgetState = bidData?.budget_state // get budget state from budget tracking with intention of determining if it was added
        let paymentReceived = bidData?.inventory.total_received // get project total payment

        if (budgetState != 'réel') {
            toast({
                component: ToastificationContent,
                props: {
                    title: "Devis",
                    text: 'Veuillez enregistrer le vendu et passez au suivi réel',
                    icon: "AlertTriangleIcon",
                    variant: "warning",
                },
            });
            return;
        }

        if (!paymentReceived || paymentReceived === 0) {
            toast({
                component: ToastificationContent,
                props: {
                    title: "État de paiement",
                    text: "Aucun paiement n'a été enregistré pour cette offre",
                    icon: "AlertTriangleIcon",
                    variant: "warning",
                },
            });
            return;
        }
        Swal.fire({
            title: "Ce projet disparaitra de la liste des projets !",
            text: "Assurez-vous que les factures ont été payé et les doocuements du projet sont archivés.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, continuer!",
            cancelButtonText: "Annuler",
            customClass: {
                confirmButton: "btn btn-danger",
                cancelButton: "btn btn-outline-primary ml-1",
            },
            buttonsStyling: false,
            preConfirm() {
                const uid = props.bidData?.value.uid;
                return axios
                    .put(`toggle/projects/${uid}/close`, {
                        headers: {
                            Accept: "application/vnd.api+json",
                            "Content-Type": "application/vnd.api+json",
                        },
                    })
                    .then(({data, status}) => {
                        if (status !== 200) throw new Error(data.message);
                        return data.resource;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(
                            "Une erreur est survenue lors de la modification de l'offre."
                        );
                    });
            },
        }).then((result) => {
            if (result.value) {
                Swal.fire({
                    icon: "success",
                    title: "Fermé!",
                    text: "L'offre a été clôturée.",
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                }).then((result) => {
                    router.go(-1);
                });
            }
        });
    };

    const checkInventoryAttribute = (key) => {
        let inventory = props.bidData.value?.inventory;
        if(!inventory) return false;
        return inventory.hasOwnProperty(key) && inventory[key] === true;
    }

    const isLost = computed(() => {
        return checkInventoryAttribute('set_as_lost');
    });

    const isClosed = computed(() => {
        return checkInventoryAttribute('is_closed');
    });

    return {
        toggleAsWin,
        toggleAsLost,
        closeProject,
        isLost,
        isClosed,
    };
}
